import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, TextField, Button, Grid, Typography, Divider, FormControlLabel, Checkbox } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2, 3, 3),
    borderRadius: 20,
    background: 'linear-gradient(135deg,#ffffff 0%, #fafbff 100%)',
    boxShadow: '0 10px 28px -4px rgba(32,40,52,0.10), 0 4px 12px -2px rgba(32,40,52,0.06)',
    position: 'relative',
  overflow: 'hidden',
  maxHeight: 'calc(100vh - 220px)',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: -140,
      left: -140,
      width: 320,
      height: 320,
      background: 'radial-gradient(circle at 55% 55%, rgba(66,133,244,0.20), rgba(66,133,244,0) 70%)',
      borderRadius: '50%',
      filter: 'blur(4px)',
      opacity: 0.7,
      pointerEvents: 'none'
    }
  },
  titleWrap: { marginBottom: theme.spacing(2) },
  title: {
    fontWeight: 600,
    letterSpacing: '-0.5px',
    background: 'linear-gradient(90deg,#1d1f27 0%, #2f3b55 100%)',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    display: 'flex',
    alignItems: 'center',
    minHeight: 40
  },
  sectionLabel: {
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: '0.12em',
    fontWeight: 600,
    color: '#7d8694',
    marginBottom: theme.spacing(1.2)
  },
  // sectionBox supprimé : on enlève le fond blanc pour un rendu plus léger comme GoldForm
  field: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      background: '#fff',
      transition: 'box-shadow .25s, border-color .25s',
      '& fieldset': { borderColor: '#dfe3ea' },
      '&:hover fieldset': { borderColor: '#b8c2cf' },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px rgba(66,133,244,0.25)',
        '& fieldset': { borderColor: '#4285f4' }
      }
    },
  '& .MuiInputLabel-outlined.Mui-focused': { color: '#1d60d6' },
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance:'none', margin:0 },
    '& input[type=number]': { MozAppearance:'textfield' }
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    borderTop: '1px solid #e7ebf0'
  },
  primaryBtn: {
    borderRadius: 14,
    padding: '10px 20px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '.3px',
    background: 'linear-gradient(90deg,#1a73e8 0%,#1967d2 100%)',
    boxShadow: '0 4px 14px -2px rgba(26,115,232,0.35)',
    '&:hover': { background: 'linear-gradient(90deg,#1b6fdc 0%,#155fc2 100%)' }
  },
  resetBtn: { borderRadius: 14, textTransform: 'none', fontWeight: 500 },
  subtleDivider: {
    margin: theme.spacing(1.5, 0, 2),
    background: 'linear-gradient(90deg,rgba(0,0,0,0) 0%, #dfe4ea 50%, rgba(0,0,0,0) 100%)',
    height: 1,
    border: 'none'
  }
}));

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const ClalForm = ({ metaux = [], onAdd, editing = false, initialValuesData = null, onCancelEdit }) => {
  const classes = useStyles();
  const defaultMetal = metaux.find(m => String(m.id_metal) === '1') || metaux[0] || null;
  const baseInit = useMemo(()=> ({
    id: null,
    date: today(),
    designation: '',
    note: '',
    metal: defaultMetal ? defaultMetal.id_metal : null,
    entreeFin: '',
    sortie750: '',
    sortieFin: '',
    achat: false,
    prixAchat: ''
  }), [defaultMetal]);
  const init = initialValuesData ? { ...baseInit, ...initialValuesData } : baseInit;
  const [overrideSortieFin, setOverrideSortieFin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // refs pour navigation Enter (ordre: date -> designation -> note -> métal -> sortie750 -> sortieFin -> entreeFin selon cas or / metal)
  const designationRef = useRef(null);
  const noteRef = useRef(null);
  const metalRef = useRef(null);
  const entreeRef = useRef(null);
  const sortie750Ref = useRef(null);
  const sortieFinRef = useRef(null);

  const focusNext = (current, isGold) => {
    const orderGold = [dateRef, designationRef, noteRef, metalRef, entreeRef, sortie750Ref, sortieFinRef];
    const orderOther = [dateRef, designationRef, noteRef, metalRef, entreeRef, sortie750Ref];
    const arr = isGold ? orderGold : orderOther;
    const idx = arr.findIndex(r => r === current);
    if (idx >= 0 && idx < arr.length -1) {
      const next = arr[idx+1];
      if(next && next.current) { try { next.current.focus(); if(next.current.select) next.current.select(); } catch(_){} }
    }
  };

  const dateRef = useRef(null);
  useEffect(()=>{ if(dateRef.current) { try{ dateRef.current.focus(); }catch(_){/* ignore */} } },[]);

  const normalizeNum = (o) => {
    if(o===''||o===null||o===undefined) return null;
    const n = parseFloat(String(o).replace(',','.'));
    return isNaN(n) ? null : n;
  };
  const validationSchema = Yup.object({
    date: Yup.string().required('Date requise'),
    designation: Yup.string().required('Désignation requise'),
    note: Yup.string().nullable(),
    metal: Yup.mixed().required('Métal requis'),
    entreeFin: Yup.number().transform((v,o)=> normalizeNum(o)).min(0,'>=0').nullable(true),
    sortie750: Yup.number().transform((v,o)=> normalizeNum(o)).min(0,'>=0').nullable(true),
    sortieFin: Yup.number().transform((v,o)=> normalizeNum(o)).min(0,'>=0').nullable(true),
    achat: Yup.boolean(),
    prixAchat: Yup.number().transform((v,o)=> normalizeNum(o)).min(0,'>=0').nullable(true)
  }).test('au-moins-un','Renseigner au moins un champ', function(values){
    if(!values) return false;
    const {entreeFin, sortie750, sortieFin} = values;
    const has = [entreeFin, sortie750, sortieFin].some(v => {
      if(v===null||v===undefined||v==='') return false; const n = parseFloat(String(v).replace(',','.')); return !isNaN(n) && n!==0;
    });
    if(!has) return this.createError({path:'entreeFin', message:'Renseigner au moins un champ'});
    return true;
  });

  // Limitation & format: conserver la virgule comme séparateur (xxxx,xx)
  // Limitation & format générique: maxInt configurable (par défaut 4), 2 décimales
  const limitNumeric = (raw, maxInt = 4) => {
    if(raw===''||raw===null||raw===undefined) return '';
    // Supprimer espaces classiques et insécables (U+00A0, U+202F)
    let v = String(raw).replace(/[\s\u00A0\u202F]+/g,'');
    // supprimer caractères non valides
    v = v.replace(/[^0-9.,]/g,'');
    // ne garder que le premier séparateur décimal si plusieurs
    const firstSepIndex = v.search(/[.,]/);
    let sepChar = null;
    if(firstSepIndex !== -1){
      sepChar = v[firstSepIndex];
      const before = v.slice(0, firstSepIndex); // sans le séparateur
      let after = v.slice(firstSepIndex+1).replace(/[.,]/g,'');
      v = before + sepChar + after;
    }
    // Découper sur séparateur (point ou virgule)
    const parts = v.split(/[.,]/);
    let int = parts[0] || '';
    let dec = parts[1] || '';
    if(int.length>maxInt) int = int.slice(0,maxInt);
    if(dec.length>2) dec = dec.slice(0,2);
    if(sepChar === null) return int;
    return dec.length ? `${int}${sepChar}${dec}` : `${int}${sepChar}`; // on laisse le séparateur même si pas encore de décimales pour permettre suite
  };

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.titleWrap}>
        <Typography variant="h4" className={classes.title} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
          <span>CLAL</span>
          <span style={{ width: editing ? 'auto':'0px', overflow:'hidden', transition:'width .25s', marginLeft: editing ? 8 : 0, whiteSpace:'nowrap', fontSize:'0.70em', fontWeight:600, letterSpacing:'0.5px', padding: editing ? '4px 10px':0, borderRadius:12, background: editing ? '#e3edfb':'transparent', color:'#1a4f9c' }}>{editing ? 'ÉDITION' : ''}</span>
        </Typography>
      </div>
      <Formik
        enableReinitialize
        initialValues={init}
        validationSchema={validationSchema}
        onSubmit={async (values,{resetForm})=>{
          const isGold = String(values.metal) === '1';
          const entree_or_fin = values.entreeFin ? parseFloat(String(values.entreeFin).replace(',','.')) : 0;
          const sortie_or_750 = values.sortie750 ? parseFloat(String(values.sortie750).replace(',','.')) : 0;
          const sortie_or_fin = isGold ? (values.sortieFin ? parseFloat(String(values.sortieFin).replace(',','.')) : 0) : (values.sortie750 ? parseFloat(String(values.sortie750).replace(',','.')) : 0);
          const row = {
            id: editing && init.id ? init.id : Date.now(),
            date_mouvement: values.date,
            designation: values.designation,
            note: values.note || '',
            metal_id: values.metal || 1,
            entree_or_fin,
            sortie_or_750,
            sortie_or_fin,
            perte: 0,
            achat: !!values.achat,
            prix_achat: values.achat ? (values.prixAchat ? parseFloat(String(values.prixAchat).replace(',','.')) : 0) : null,
            __source: 'CLAL'
          };
          if(onAdd){
            setSubmitting(true); setSubmitError(null);
            try {
              const maybePromise = onAdd(row, editing);
              if(maybePromise && typeof maybePromise.then === 'function') {
                await maybePromise;
              }
              if(!editing){
                resetForm({values:{...baseInit, date: today(), metal: baseInit.metal}});
                setOverrideSortieFin(false);
              }
              // Re-focus sur la date après succès (création ou édition)
              setTimeout(()=>{ try { if(dateRef.current) dateRef.current.focus(); } catch(_){} }, 30);
            } catch(e){
              setSubmitError('Erreur lors de la sauvegarde ' + (e && e.message ? '('+ e.message +')' : ''));
            } finally {
              setSubmitting(false);
            }
          }
        }}
      >
      {({values, errors, touched, handleChange, setFieldValue, submitForm})=>{
        const isGold = String(values.metal) === '1';
        // Auto-calcul uniquement en création (pas en édition) pour ne pas écraser la valeur stockée en base
        if(isGold && !overrideSortieFin && !editing){
          const v = parseFloat(String(values.sortie750).replace(',','.'));
          const computed = !isNaN(v) ? (v*0.75).toFixed(2) : '';
          if(values.sortieFin !== (computed ? computed.replace('.',',') : '')){ setFieldValue('sortieFin', computed ? computed.replace('.',',') : '', false); }
        }
        const clamp = (raw)=>{ if(raw===''||raw===null) return raw; const n=parseFloat(String(raw).replace(',','.')); if(isNaN(n)) return ''; if(n<0) return '0'; return raw; };
        return (<Form>
  <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.sectionLabel}>Informations générales</div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Date"
            type="date"
            name="date"
            value={values.date}
            onChange={handleChange}
            inputRef={dateRef}
            variant="outlined" size="small" className={classes.field}
            InputLabelProps={{ shrink:true }}
            onFocus={e=>{ e.target.select && e.target.select(); }}
            onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); focusNext(dateRef, isGold); } }}
            error={touched.date && Boolean(errors.date)}
            helperText={touched.date && errors.date}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Désignation"
            name="designation"
            value={values.designation}
            onChange={e => setFieldValue('designation', e.target.value.toUpperCase().replace(/\s+/g, ''))}
            variant="outlined" size="small" className={classes.field}
            inputRef={designationRef}
            onFocus={e=>{ e.target.select && e.target.select(); }}
            onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); focusNext(designationRef, isGold); } }}
            error={touched.designation && Boolean(errors.designation)}
            helperText={touched.designation && errors.designation}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Note"
            name="note"
            value={values.note}
            onChange={handleChange}
            variant="outlined" size="small" className={classes.field}
            inputRef={noteRef}
            multiline
            rows={2}
            onFocus={e=>{ e.target.select && e.target.select(); }}
            onKeyDown={e=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); focusNext(noteRef, isGold); } }}
            error={touched.note && Boolean(errors.note)}
            helperText={touched.note && errors.note}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider className={classes.subtleDivider} />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.sectionLabel}>Mouvements Or</div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={metaux}
            getOptionLabel={(o)=> (o && (o.description || o.label || ''))}
            value={metaux.find(m => String(m.id_metal) === String(values.metal)) || null}
            onChange={(e,val)=>{ setFieldValue('metal', val ? val.id_metal : null); setFieldValue('entreeFin',''); setFieldValue('sortie750',''); setFieldValue('sortieFin',''); setOverrideSortieFin(false); setTimeout(()=>{ if(entreeRef.current){ try{entreeRef.current.focus();}catch(_){}}},0); }}
            renderInput={(params)=>(
              <TextField {...params} label="Métal" variant="outlined" size="small" className={classes.field} inputRef={metalRef} onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); focusNext(metalRef, isGold); } }} error={touched.metal && Boolean(errors.metal)} helperText={touched.metal && errors.metal} />
            )}
          />
        </Grid>
        {isGold ? (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Entrée Or Fin"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                name="entreeFin"
                value={values.entreeFin}
                onChange={e=> setFieldValue('entreeFin', clamp(limitNumeric(e.target.value)))}
                variant="outlined" size="small" className={classes.field}
                inputRef={entreeRef}
                onFocus={e=>{ e.target.select && e.target.select(); }}
                onKeyDown={e=>{ if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if(e.key==='Enter'){ e.preventDefault(); focusNext(entreeRef, isGold); } }}
                error={touched.entreeFin && Boolean(errors.entreeFin)}
                helperText={touched.entreeFin && errors.entreeFin}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sortie Or 750"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                name="sortie750"
                value={values.sortie750}
                onChange={e=> setFieldValue('sortie750', clamp(limitNumeric(e.target.value)))}
                variant="outlined" size="small" className={classes.field}
                inputRef={sortie750Ref}
                onFocus={e=>{ e.target.select && e.target.select(); }}
                onKeyDown={e=>{ if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if(e.key==='Enter'){ e.preventDefault(); focusNext(sortie750Ref, isGold); } }}
                error={touched.sortie750 && Boolean(errors.sortie750)}
                helperText={touched.sortie750 && errors.sortie750}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sortie Or Fin"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                name="sortieFin"
                value={values.sortieFin}
                onChange={e=>{ setFieldValue('sortieFin', clamp(limitNumeric(e.target.value))); setOverrideSortieFin(true); }}
                variant="outlined" size="small" className={classes.field}
                inputRef={sortieFinRef}
                onFocus={e=>{ e.target.select && e.target.select(); }}
                onKeyDown={e=>{ if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if(e.key==='Enter'){ e.preventDefault(); submitForm(); } }}
                error={touched.sortieFin && Boolean(errors.sortieFin)}
                helperText={touched.sortieFin && errors.sortieFin}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Entrée Métal"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                name="entreeFin"
                value={values.entreeFin}
                onChange={e=> setFieldValue('entreeFin', clamp(limitNumeric(e.target.value)))}
                variant="outlined" size="small" className={classes.field}
                inputRef={entreeRef}
                onFocus={e=>{ e.target.select && e.target.select(); }}
                onKeyDown={e=>{ if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if(e.key==='Enter'){ e.preventDefault(); focusNext(entreeRef, isGold); } }}
                error={touched.entreeFin && Boolean(errors.entreeFin)}
                helperText={touched.entreeFin && errors.entreeFin}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sortie Métal"
                type="text"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                name="sortie750"
                value={values.sortie750}
                onChange={e=> setFieldValue('sortie750', clamp(limitNumeric(e.target.value)))}
                variant="outlined" size="small" className={classes.field}
                inputRef={sortie750Ref}
                onFocus={e=>{ e.target.select && e.target.select(); }}
                onKeyDown={e=>{ if(['ArrowUp','ArrowDown','-'].includes(e.key)){ e.preventDefault(); } if(e.key==='Enter'){ e.preventDefault(); submitForm(); } }}
                error={touched.sortie750 && Boolean(errors.sortie750)}
                helperText={touched.sortie750 && errors.sortie750}
              />
            </Grid>
          </>
        )}

        {/* Section Achat en fin de formulaire */}
        <Grid item xs={12}>
          <Divider className={classes.subtleDivider} />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.sectionLabel}>Achat</div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={<Checkbox color="primary" checked={values.achat} onChange={e=> setFieldValue('achat', e.target.checked)} tabIndex={-1} />}
            label="Achat"
          />
        </Grid>
        {values.achat && (
          <Grid item xs={12} sm={6}>
            <TextField
              label="Prix d'achat"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*[.,]?[0-9]*"
              name="prixAchat"
              value={values.prixAchat}
              onChange={e=> setFieldValue('prixAchat', limitNumeric(e.target.value, 6))}
              variant="outlined" size="small" className={classes.field}
              onFocus={e=>{ e.target.select && e.target.select(); }}
              onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); submitForm(); } }}
              error={touched.prixAchat && Boolean(errors.prixAchat)}
              helperText={touched.prixAchat && errors.prixAchat}
            />
          </Grid>
        )}
      </Grid>
      <div className={classes.actions}>
        <Button className={classes.resetBtn} variant="outlined" onClick={() => { if(editing){ if(onCancelEdit) onCancelEdit(); } else { setOverrideSortieFin(false); } }}>{editing ? 'Annuler' : 'Réinitialiser'}</Button>
        <Button className={classes.primaryBtn} color="primary" variant="contained" disabled={submitting} onClick={submitForm}>{submitting ? (editing ? 'Mise à jour...' : 'Ajout...') : (editing ? 'Mettre à jour' : 'Ajouter')}</Button>
      </div>
      {submitError && <div style={{color:'#c62828', fontSize:13, marginTop:8}}>{submitError}</div>}
      </Form>
      );}}
      </Formik>
    </Paper>
  );
};

ClalForm.propTypes = {
  metaux: PropTypes.array,
  onAdd: PropTypes.func,
  editing: PropTypes.bool,
  initialValuesData: PropTypes.object,
  onCancelEdit: PropTypes.func
};

export default ClalForm;
